<script setup>
import FullCalendar from '@fullcalendar/vue3'
import frLocale from '@fullcalendar/core/locales/fr';
import dayGridPlugin from '@fullcalendar/daygrid';
import listPlugin from "@fullcalendar/list";
import timeGridPlugin from "@fullcalendar/timegrid";
import {computed, onBeforeMount, onBeforeUnmount, ref, watch} from "vue";
import {useHttpErrorsHandler} from "../../Composables/httpErrorsHandler.js";
import {useAuthUserStore} from "../../Store/AuthUserStore.js";
import {useSubscriptionStore} from "../../Store/SubscriptionStore.js";
import axios from "axios";
import {useRouter} from "vue-router";
import {getHolidaysAsEvents} from "../../Utils/holidays.js";
import FormSelectCenters from "../../Components/FormSelectCenters.vue";
import {MODULES} from "../../Constants/modules.js";

const { httpErrorsHandler } = useHttpErrorsHandler();
const authUserStore = useAuthUserStore();
const subscriptionStore = useSubscriptionStore();
const authUser = computed(() => authUserStore.authUser);
const router = useRouter();

// Module access helper
const hasModuleAccess = (moduleSlug) => subscriptionStore.hasModuleAccess(moduleSlug);

const loading = ref(true);
const errors = ref(null);
const events = ref([]);
const startDateSave =  ref(null);
const endDateSave = ref(null);
const eventToShow = ref(null);
const upcomingEvents = ref([]);
const calendarOptions = {
    timeZone: 'local',
    themeSystem: 'bootstrap',
    initialView: localStorage.getItem('clientRequestAgendaView') || 'timeGridWeek',
    initialDate: localStorage.getItem('clientRequestAgendaStartDate') || new Date().toISOString().split('T')[0], // Utilise la date enregistrée ou aujourd'hui
    plugins: [dayGridPlugin, listPlugin, timeGridPlugin],
    weekends: true,
    firstDay: 1,
    headerToolbar: {
        start: 'today prev,next',
        center: 'title',
        end: 'timeGridWeek,timeGridThreeDay,timeGridDay,listMonth'
    },
    eventTimeFormat: {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
    },
    events: events.value,
    expandRows: true,
    displayEventTime: true,
    nowIndicator: true,
    locale: frLocale,
    eventColor: '#ffffff',
    scrollTime: new Date().toTimeString().slice(0, 8),
    eventOverlap: false,
    slotEventOverlap: false,
    allDaySlot : true,
    height: '100%',
    slotMinTime: '07:00:00',
    slotMaxTime: '20:00:00',
    datesSet: async (dateInfo) => {
        localStorage.setItem('clientRequestAgendaStartDate', dateInfo.startStr);
        localStorage.setItem('clientRequestAgendaEndDate', dateInfo.endStr);
        localStorage.setItem('clientRequestAgendaView', dateInfo.view.type);
        getEvents(dateInfo.startStr, dateInfo.endStr);
    },
    eventClick: async (clickInfo) => {
        // Ignorer le clic sur les jours fériés
        if (clickInfo.event.extendedProps.isHoliday) {
            return;
        }
        const event = events.value.find(event => parseInt(event.id) === parseInt(clickInfo.event.id));
        if (!event || !event.data || event.data.client_request_id === null) {
            return;
        }
        await router.push({name: 'client-requests.show', params: {id: event.data.client_request_id}});
    },
    eventContent: function(arg) {
        let titleWithCenter = arg.event.extendedProps.center ? arg.event.title + ' - ' + arg.event.extendedProps.center : arg.event.title;

        if (arg.view.type === 'listMonth') {
            return { html: titleWithCenter };
        } else {
            return { html: arg.event.title };
        }
    },
    eventDidMount: function(arg) {
        if (arg.view.type === 'listMonth') {
            const color = arg.event.backgroundColor || '#212529';
            const dot = arg.el.querySelector('.fc-list-event-dot');
            if (dot) {
                dot.style.borderColor = color;
                dot.style.backgroundColor = color;
            }
        }
    },
    views: {
        timeGridThreeDay: {
            type: 'timeGrid',
            duration: { days: 3 },
            buttonText: '3 Jours'
        },
    }
};

onBeforeMount(async () => {
    // Ensure modules are loaded before checking access
    if (subscriptionStore.accessibleModules.length === 0 && !subscriptionStore.loading) {
        await subscriptionStore.fetchAccessibleModules();
    }

    loadSettingsFromLocalStorage();
    loadDateRangeFromLocalStorage();
    getUpcomingEvents();
});

onBeforeUnmount(() => {
    document.querySelectorAll('.modal').forEach(modalElement => {
        const modalInstance = bootstrap.Modal.getInstance(modalElement);
        if (modalInstance) {
            modalInstance.hide();
        }
    });
})

const selectedCenters = ref([]);
const preSelectedCenters = ref([]);
function readStoredCenters() {
    // Le stockage local peut contenir "undefined" ou un JSON invalide : on repart alors des centres de l'utilisateur.
    try {
        const stored = JSON.parse(localStorage.getItem('clientRequestAgendaSelectedCenters'));
        return Array.isArray(stored) ? stored : null;
    } catch {
        localStorage.removeItem('clientRequestAgendaSelectedCenters');
        return null;
    }
}

function loadSettingsFromLocalStorage() {
    selectedCenters.value = readStoredCenters() ?? authUser.value?.centers ?? [];
    preSelectedCenters.value = selectedCenters.value.map(center => {
        return { data: { id: center.id, name: center.name }};
    });
}

watch(selectedCenters, (newValue) => {
    localStorage.setItem('clientRequestAgendaSelectedCenters', JSON.stringify(newValue));
});

function loadEventsForSelectedCenters() {
    getEvents(startDateSave.value, endDateSave.value);
    getUpcomingEvents();
    const modal = bootstrap.Modal.getOrCreateInstance(document.getElementById('modalSelectCenters'));
    modal.hide();
}

function getEvents(startDate = null, endDate = null) {
    startDateSave.value = startDate;
    endDateSave.value = endDate;

    // Check module access before calling API
    if (!hasModuleAccess(MODULES.EVENTS)) {
        loading.value = false;
        return;
    }

    const start = new Date(startDate);
    start.setDate(start.getDate() - 1);
    const end = new Date(endDate);
    end.setDate(end.getDate() + 1);

    axios.get('/api/events', { params: {
            start_date: start,
            end_date: end,
            show_appointments_events: true,
            centers_id: selectedCenters.value.map(center => center.id)
        } })
        .then(response => {
            events.value.splice(0, events.value.length);

            // Ajouter les jours fériés
            const holidays = getHolidaysAsEvents(start, end);
            events.value.push(...holidays);

            response.data.data.forEach(event => {
                events.value.push({
                    id: event.data.id,
                    title: event.data.title,
                    start: event.data.start_date,
                    end: event.data.all_day ? event.data.end_date_all_day : event.data.end_date,
                    backgroundColor: event.data.color ?? '#212529',
                    allDay: event.data.all_day,
                    location: event.data.location,
                    data : event.data,
                    can : event.can,
                    center: event.data.center_id === null ? null : event.data.center.data.name
                });
            });
            loading.value = false;
        })
        .catch(error => {
            loading.value = false;
            httpErrorsHandler(error);
        });
}

function getUpcomingEvents() {
    // Check module access before calling API
    if (!hasModuleAccess(MODULES.EVENTS)) {
        return;
    }

    axios.get('/api/events', { params: {
            start_date: new Date(),
            take: 4,
            upcoming_appointments_events: true,
            centers_id: selectedCenters.value.map(center => center.id),
            loadClientRequest: true
        } })
        .then(response => {
            upcomingEvents.value = response.data.data;
        })
        .catch(error => {
            httpErrorsHandler(error);
        });
}

function showModalCenters(event) {
    eventToShow.value = event;
    const modal = bootstrap.Modal.getOrCreateInstance(document.getElementById('modalSelectCenters'));
    modal.show()
}

function truncateText(text, length) {
    if (!text) {
        return '';
    }
    return text.length > length ? text.substring(0, length) + '...' : text;
}

function loadDateRangeFromLocalStorage() {
    const storedStartDate = localStorage.getItem('clientRequestAgendaStartDate');
    const storedEndDate = localStorage.getItem('clientRequestAgendaEndDate');

    if (storedStartDate && storedEndDate) {
        startDateSave.value = storedStartDate;
        endDateSave.value = storedEndDate;

        // Charger les événements pour ces dates
        getEvents(storedStartDate, storedEndDate);
    }
}
</script>

<template>
    <div class="row">
        <div class="col-xl-3">
            <a
                href="#"
                class="btn btn-dark bg-gradient w-100 mb-4 align-middle "
                @click.prevent="showModalCenters"
            >
                <i class="mdi mdi-filter-menu fs-14 align-middle me-2"></i> Choix centres
            </a>
            <div>
                <h5 class="mb-1">Prochains RDV</h5>
                <p class="text-muted">Dates et heures selon le fuseau Europe/Paris.</p>
                <div class="pe-2 me-n1 mb-3">
                    <div>
                        <div class="card mb-3" v-for="(upcomingEvent, index) in upcomingEvents" :key="index">
                            <div class="card-body">
                                <a href="#" class="card-title fs-16 h6" :style="{color: upcomingEvent.data.color ?? '2d65cd'}" @click.prevent="router.push({name: 'client-requests.show', params: {id: upcomingEvent.data.client_request_id}})" v-if="upcomingEvent.data.client_request_id !== null">
                                    {{ upcomingEvent.data.title }}
                                </a>
                                <h6 class="card-title fs-16 h6" :style="{color: upcomingEvent.data.color ?? '2d65cd'}" v-else>
                                    {{ upcomingEvent.data.title }}
                                </h6>
                                <div class="event-details mt-3">

                                    <div class="d-flex align-items-center mb-1">
                                        <div class="flex-shrink-0 me-3">
                                            <i class="ri-calendar-event-line text-muted fs-16"></i>
                                        </div>
                                        <div class="flex-grow-1">
                                            <h6 class="d-block fw-semibold mb-0">
                                                <span id="event-timepicker1-tag">
                                                    {{ upcomingEvent.data.start_end_date_fr }}
                                                </span>
                                            </h6>
                                        </div>
                                    </div>
                                    <div class="d-flex align-items-center mb-1">
                                        <div class="flex-shrink-0 me-3">
                                            <i class="ri-time-line text-muted fs-16"></i>
                                        </div>
                                        <div class="flex-grow-1">
                                            <h6 class="d-block fw-semibold mb-0">
                                                <span id="event-timepicker1-tag">{{ upcomingEvent.data.all_day ? 'Journée entière' : upcomingEvent.data.start_end_hour_fr }}</span>
                                            </h6>
                                        </div>
                                    </div>
                                    <div class="d-flex align-items-center mb-1">
                                        <div class="flex-shrink-0 me-3">
                                            <i class="ri-price-tag-3-line text-muted fs-16"></i>
                                        </div>
                                        <div class="flex-grow-1">
                                            <h6 class="d-block fw-semibold mb-0"> <span >{{ upcomingEvent.data.subtype === null ? '--' : upcomingEvent.data.subtype }}</span></h6>
                                        </div>
                                    </div>

                                    <div class="d-flex align-items-center mb-1">
                                        <div class="flex-shrink-0 me-3">
                                            <i class="ri-sticky-note-line text-muted fs-16"></i>
                                        </div>
                                        <div class="flex-grow-1">
                                            <p class="d-block fw-normal mb-0 text-head" v-if="upcomingEvent.data.client_request?.data.notes !== null">
                                                <span v-html="truncateText(upcomingEvent.data.client_request?.data.notes, 100).replace(/\n/g, '<br />')" ></span>
                                            </p>
                                            <p class="d-block fw-medium mb-0 text-head" v-else>--</p>
                                        </div>
                                    </div>
                                </div>
                                <p class="text-muted text-truncate-two-lines mb-0"></p>
                            </div>
                        </div>
                        <!-- No events-->
                        <div class="mt-4 text-center" v-if="upcomingEvents.length === 0">
                            <div class="text-center">
                                <i class="mdi mdi-calendar-remove fs-36 text-muted"></i><p class="text-muted">Aucun RDV</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <!-- Modal Signature -->
            <div id="modalSelectCenters" class="modal fade" tabindex="-1" aria-labelledby="modalSelectCenters" aria-hidden="true" style="display: none;">
                <div class="modal-dialog modal-lg">
                    <div class="modal-content">
                        <div class="modal-header">
                            <h5 class="modal-title" id="modalSelectCenters">Selection de centres</h5>
                            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"> </button>
                        </div>
                        <div class="modal-body">
                            <form-select-centers
                                :errors="errors"
                                label="Centres"
                                name="centers"
                                :required="true"
                                :data="selectedCenters"
                                @update:field="selectedCenters = $event"
                                bs-class="col-md-12"
                                :disabled="false"
                                :taggable="false"
                                :label-hidden="false"
                                :min="1"
                                :centers-pre-selected="preSelectedCenters"
                                :allow-empty="false"
                                placeholder="Sélectionnez un ou plusieurs centres"
                            />
                        </div>
                        <div class="modal-footer">
                            <button type="button" class="btn btn-soft-dark fw-medium text-decoration-none shadow-none" data-bs-dismiss="modal">Fermer</button>
                            <button type="button" class="btn btn-secondary fw-medium text-decoration-none shadow-none" @click="loadEventsForSelectedCenters">Valider</button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div class="col-xl-9">
            <div class="card">
                <div class="card-body body-calendar">
                    <FullCalendar :options='calendarOptions' ref="calendarRef" />
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
.body-calendar{
    height: 83.5vh !important;
}
</style>
